/**
 * Admin Licence Detail: pure business rules.
 *
 * Framework-free helpers (no React/Redux/fetch) so they are trivially unit-tested
 * as plain functions. The slice/saga/UI compose these; they never re-implement the
 * logic inline.
 */

import { MAX_PRIMARY_CONTACTS } from '@shared/constants/primary-contacts';
import { SUBSCRIPTION_STATUS } from '@shared/constants/subscriptionStatus';
import type { ListResponse, PrimaryContact, UserResponse } from '@shared/schemas';

import {
  ACCESS_STATE_ORDER,
  type AccessState,
  accessState,
} from '@/features/user/_shared/domain/access-state';

/** Lists shown most-recent first (by start date, descending). Returns a new array. */
export function sortListsByRecency(lists: ListResponse[]): ListResponse[] {
  return [...lists].sort((a, b) => b.start_date.localeCompare(a.start_date));
}

/**
 * `3f9c2a1e-…-5d0b6a7fb7d4`: a uuid with its three middle groups elided, the way
 * the panel shows a licence id: both ends stay recognisable, the copy button hands
 * over the whole, and it fits the panel at every root font size (the full middle
 * ran into an ellipsis of its own at 1920px). Anything that is not a five-group
 * uuid is shown as is.
 */
export function shortenLicenceId(id: string): string {
  const groups = id.split('-');
  if (groups.length !== 5) return id;
  return `${groups[0]}-…-${groups[4]}`;
}

/**
 * The licence's initials for its avatar: the first letters of its first two
 * words ("Acme Capital" → "AC"), or the first two letters of a one-word name
 * ("Inovexus" → "IN"). `'?'` for a blank name, like `initialsOf`.
 */
export function licenceInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const letters = words.length === 1 ? [...words[0]].slice(0, 2) : [[...words[0]][0], [...words[1]][0]];
  return letters.join('').toUpperCase();
}

// ── Main contacts (#564) ──

/**
 * Who may be added as a main contact: an active, non-archived user of the licence
 * who is not one already. The rows are the licence's users as the Users tab
 * loads them; the backend applies the same rule to whatever id is sent.
 */
export function primaryContactCandidates(
  users: readonly UserResponse[],
  contacts: readonly PrimaryContact[],
): UserResponse[] {
  const taken = new Set(contacts.map((c) => c.id));
  return users.filter(
    (u) =>
      u.subscription_status === SUBSCRIPTION_STATUS.ACTIVE && !u.archived_at && !taken.has(u.id),
  );
}

/**
 * Why "Add contact" cannot be used right now, or `null` when it can; the
 * sentence the disabled button's tooltip shows.
 */
export function addContactBlocker(
  contacts: readonly PrimaryContact[],
  candidates: readonly UserResponse[],
): string | null {
  if (contacts.length >= MAX_PRIMARY_CONTACTS) {
    return `A licence has at most ${MAX_PRIMARY_CONTACTS} main contacts. Remove one to add another.`;
  }
  if (candidates.length === 0) {
    return 'No active user to add. A main contact must have activated their account.';
  }
  return null;
}

/** The contact ids to send once `userId` is added; the endpoint takes the whole set. */
export function contactIdsWith(contacts: readonly PrimaryContact[], userId: string): string[] {
  const ids = contacts.map((c) => c.id);
  return ids.includes(userId) ? ids : [...ids, userId];
}

/** The contact ids to send once `userId` is removed. */
export function contactIdsWithout(contacts: readonly PrimaryContact[], userId: string): string[] {
  return contacts.map((c) => c.id).filter((id) => id !== userId);
}

/** Who is still waiting to get in, and what the banner under the table says. */
export interface InvitationBacklog {
  /** Invited, not activated yet. */
  pending: number;
  /** Never invited at all. */
  neverInvited: number;
  total: number;
}

/**
 * Who on this licence is still waiting to get in (#574).
 *
 * A deactivated (archived) account is never counted: its access is cut on
 * purpose, and offering to invite it would say otherwise.
 */
export function invitationBacklog(users: readonly UserResponse[]): InvitationBacklog {
  const live = users.filter((u) => !u.archived_at);
  const pending = live.filter((u) => u.subscription_status === SUBSCRIPTION_STATUS.PENDING).length;
  const neverInvited = live.filter(
    (u) => u.subscription_status === SUBSCRIPTION_STATUS.NO_ACCESS,
  ).length;
  return { pending, neverInvited, total: pending + neverInvited };
}

const people = (n: number) => `${n} ${n === 1 ? 'user' : 'users'}`;

/**
 * `Send 5 invitations`: the button's label, counted.
 *
 * The count belongs on the button because the button is what gets read: it
 * says how many emails the click sends, at the moment of clicking. `Resend`
 * only when every one of them is a second attempt; see `invitationBannerText`.
 */
function invitationAction(verb: 'Send' | 'Resend', count: number): string {
  return `${verb} ${count} ${count === 1 ? 'invitation' : 'invitations'}`;
}

/**
 * What the band above the table says, and what its button does.
 *
 * Three populations, three sentences: a chase ("haven't activated"), a first
 * send ("never been invited"), and the mix, which names neither and states the
 * fact they share. `null` when nobody is waiting; the band is then absent,
 * rather than present and reassuring.
 *
 * The count on the button is always `total`, and in each of the three branches
 * that is also the count the sentence names: a chase has no never-invited, a
 * first send has nobody pending.
 */
export function invitationBannerText(
  backlog: InvitationBacklog,
): { message: string; action: string } | null {
  const { pending, neverInvited, total } = backlog;
  if (total === 0) return null;
  if (neverInvited === 0) {
    return {
      message: `${people(pending)} ${pending === 1 ? "hasn't" : "haven't"} activated their account yet.`,
      action: invitationAction('Resend', total),
    };
  }
  if (pending === 0) {
    return {
      message: `${people(neverInvited)} ${neverInvited === 1 ? 'has' : 'have'} never been invited.`,
      action: invitationAction('Send', total),
    };
  }
  return {
    message: `${people(total)} ${total === 1 ? "doesn't" : "don't"} have access yet.`,
    // Mixed: some are a first send, some a reminder. "Send" is the verb that is
    // true of both; "Resend" would be false for the never-invited half.
    action: invitationAction('Send', total),
  };
}

/**
 * Optimistically flip the given users to PENDING after a bulk invitation send.
 * Mirrors the pre-migration `markInvitationsAsSent`, but pure over the slice's
 * users array instead of a React setState. Returns a new array.
 */
export function markUsersAsSent(users: UserResponse[], sentIds: string[]): UserResponse[] {
  const ids = new Set(sentIds);
  return users.map((u) =>
    ids.has(u.id) ? { ...u, subscription_status: SUBSCRIPTION_STATUS.PENDING } : u,
  );
}

/** One access state, with how many of the licence's users are in it. */
export interface AccessStateCount {
  state: AccessState;
  count: number;
}

/**
 * Every access state, in reading order, with how many users are at it; zeros
 * kept, so the legend under the bar always names the whole scale.
 *
 * The same `accessState` reading the Access column and the Status filter use,
 * so the bar, the chips and the dropdown can never disagree about a row. Twin
 * of the Lists tab's `statusBreakdown`.
 */
export function accessBreakdown(users: readonly UserResponse[]): AccessStateCount[] {
  return ACCESS_STATE_ORDER.map((state) => ({
    state,
    count: users.filter((u) => accessState(u) === state).length,
  }));
}

/**
 * `15 users`: the heading's line; the access bar under it holds the breakdown.
 *
 * It used to spell out every state ("15 users · 5 active · 3 invitation sent
 * · …"), which the bar now says better, two lines lower and in proportion.
 * Twin of the Lists tab's `listsTotal`.
 */
export function usersTotal(users: readonly UserResponse[]): string {
  return `${users.length} ${users.length === 1 ? 'user' : 'users'}`;
}
